import { Menu, Sun, Moon, LogOut, UserCog, X } from 'lucide-react';
import { useThemeStore } from '@/store/themeStore';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { UserRole } from '@/types';

const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  pm_admin: 'PM Admin',
  equipment_admin: 'Equipment Admin',
  repair_tech: 'Repair Tech',
  submitter: 'Submitter',
  accounting: 'Accounting',
  ext_vendor: 'Vendor',
};

const ROLE_COLORS: Record<UserRole, string> = {
  super_admin: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  pm_admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  equipment_admin: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  repair_tech: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  submitter: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  accounting: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  ext_vendor: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
};

const ALL_ROLES: UserRole[] = [
  'super_admin',
  'pm_admin',
  'equipment_admin',
  'repair_tech',
  'submitter',
  'accounting',
  'ext_vendor',
];

interface TopNavProps {
  onMenuClick: () => void;
}

export default function TopNav({ onMenuClick }: TopNavProps) {
  const { theme, toggleTheme } = useThemeStore();
  const { currentUser, realUser, isImpersonating, signOut, impersonateRole } = useAuth();

  const isSuperAdmin = realUser?.role === 'super_admin';

  return (
    <div className="sticky top-0 z-10 flex flex-col">
      <header className="flex h-16 items-center gap-3 border-b border-border bg-card px-4 shadow-sm">
        {/* Hamburger (mobile only) */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onMenuClick}
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* App title */}
        <h1 className="text-base font-semibold text-foreground hidden sm:block">
          FixFlowIQ
        </h1>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Role impersonator (super_admin only) */}
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <UserCog className="h-4 w-4 text-muted-foreground shrink-0" />
            <Select
              value={currentUser?.role ?? realUser?.role ?? ''}
              onValueChange={(val) =>
                impersonateRole(val === realUser?.role ? null : (val as UserRole))
              }
            >
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue placeholder="View as role…" />
              </SelectTrigger>
              <SelectContent align="end">
                {ALL_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {ROLE_LABELS[role]}
                    {role === realUser?.role ? ' (you)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* User info */}
        {currentUser && (
          <div className="flex items-center gap-2">
            <span className="hidden sm:block text-sm font-medium text-foreground">
              {realUser?.name}
            </span>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                ROLE_COLORS[currentUser.role],
              )}
            >
              {ROLE_LABELS[currentUser.role]}
            </span>
          </div>
        )}

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
        </Button>

        {/* Sign out */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => signOut()}
          aria-label="Sign out"
        >
          <LogOut className="h-5 w-5" />
        </Button>
      </header>

      {/* Impersonation banner */}
      {isImpersonating && currentUser && (
        <div className="flex items-center justify-between gap-2 bg-amber-400 dark:bg-amber-500 px-4 py-1.5 text-sm font-medium text-amber-950">
          <span>
            Viewing as: <strong>{ROLE_LABELS[currentUser.role]}</strong> — nav, permissions, and
            sidebar reflect this role. API calls still use your super admin token.
          </span>
          <button
            className="flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold hover:bg-amber-500 dark:hover:bg-amber-600 transition-colors"
            onClick={() => impersonateRole(null)}
          >
            <X className="h-3.5 w-3.5" />
            Exit
          </button>
        </div>
      )}
    </div>
  );
}
